import React, { useCallback, useEffect, useState, useContext } from 'react';

import { api } from 'services/api';
import { formatDate } from 'shared/date';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  Flex,
  Text,
  Grid,
  HStack,
  useMediaQuery,
  Skeleton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Textarea,
  FormControl,
  FormLabel,
  Divider,
  Box,
} from '@chakra-ui/react';

import AppTable from 'components/AppTable';
import { ModalRootProps } from 'components/Modal/Root';
import PaginationWrapper from 'components/Pagination';

import AuthContext from 'contexts/AuthContext';

import useThrottledState from 'hooks/useThrottledState';

import { User } from 'types/access-control';
import { Budget } from 'types/budget';
import { Pagination, ServicePagination } from 'types/pagination';

import FlowStatusBadge from './StatusBadge';

interface ModalBudgetHistoryProps extends ModalRootProps {
  data: {
    budget: Budget;
    initialTab?: 'notes' | 'history';
  };
}

type FlowHistory = {
  id: number;
  type: string;
  user: {
    id: number;
    name: string;
    email: string;
    profile_verified: true;
    corporate_name: null;
    fantasy_name: null;
    document_type: null;
    document: null;
    address: null;
    number: null;
    complement: null;
    district: null;
    zip_code: null;
    city: null;
    created_at: string;
    updated_at: string;
    deleted_at: null;
  };
  object: {
    status: {
      color: string;
      icon: string;
      name: string;
    };
  };
  status: {
    color: string;
    icon: string;
    name: string;
  };
  message: string;
  created_at: string;
  updated_at: string;
};

type FlowUser = FlowHistory['user'];

const modalMargin = 120;
const modalMobileFix = 100;
const tabIndexes = {
  notes: 0,
  history: 1,
} as const;

const ModalBudgetHistory: React.FC<ModalBudgetHistoryProps> = ({
  handleClose,
  data,
  ...restProps
}) => {
  const { user } = useContext(AuthContext);
  const [budget, setBudget] = useState({} as Budget);
  const [activeTab, setActiveTab] = useState(
    data?.initialTab === 'notes' ? tabIndexes.notes : tabIndexes.history,
  );
  const [noteMessage, setNoteMessage] = useState('');

  const [isMobile] = useMediaQuery('(max-width: 728px)');

  const [history, setHistory] = useState<FlowHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingBudget, setLoadingBudget] = useState(true);
  const [isSavingNote, setIsSavingNote] = useState(false);

  const [pagination, setPagination] = useThrottledState<Pagination>(
    (() => {
      return {
        per_page: 20,
        page: 1,
      };
    })(),
    1000,
  );

  const [servicePagination, setServicePagination] = useState<ServicePagination>(
    { last_page: 1 },
  );

  const getBudget = useCallback(async () => {
    setLoadingBudget(true);
    setBudget({} as Budget);
    try {
      const { data: budgetResponse } = await api.get(
        `/budget/${data.budget.id}`,
        {
          params: {
            organization_id: user.managed_organization.organization.id,
          },
        },
      );

      const budgetData = budgetResponse.data;
      setBudget(budgetData);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao tentar carregar o orçamento',
        status: 'error',
      });

      setBudget({} as Budget);
    } finally {
      setLoadingBudget(false);
    }
  }, [user, data]);

  const getData = useCallback(async () => {
    if (!budget.id) return;

    setLoading(true);
    setHistory([]);

    try {
      const response = await api.get('/flow/history', {
        params: {
          'filter[object_id]': budget.id,
          'filter[object_type]': 'budget',
          page: pagination.page,
          per_page: pagination.per_page,
          sort: '-created_at',
        },
      });

      const historyData = response.data;

      const newPagination = {
        last_page: historyData.meta.last_page,
      };

      setHistory(historyData.data);
      setServicePagination(newPagination);
    } catch (err) {
      setHistory([]);
      setServicePagination({ last_page: 1 });
    } finally {
      setLoading(false);
    }
  }, [pagination, budget]);

  useEffect(() => {
    if (pagination.page > servicePagination?.last_page) {
      setPagination((oldPagination) => {
        if (oldPagination.page > 1) {
          return {
            ...oldPagination,
            page: 1,
          };
        }

        return oldPagination;
      });
    }
  }, [pagination.page, servicePagination, setPagination]);

  useEffect(() => {
    getBudget();
  }, [getBudget]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleConfirm = (): void => {
    handleClose();
  };

  const getBudgetUserName = (item: User): string => {
    if (item.document_type === 'cnpj') {
      return `${item.corporate_name} - ${item.fantasy_name}`;
    }

    return item.name;
  };

  const getUserName = (item: FlowUser): string => {
    if (item.document_type === 'cnpj') {
      return `${item.corporate_name} - ${item.fantasy_name}`;
    }

    return item.name;
  };

  const handleSaveNote = useCallback(async (): Promise<void> => {
    const trimmedMessage = noteMessage.trim();

    if (!trimmedMessage) {
      toast({
        description: 'Adicione uma observação antes de salvar.',
        status: 'warning',
      });
      return;
    }

    try {
      setIsSavingNote(true);
      await api.post('/flow/history', {
        object_type: 'budget',
        object_id: budget.id,
        type: 'manual',
        message: trimmedMessage,
      });

      getData();
      setActiveTab(tabIndexes.history);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao tentar salvar a observação.',
        status: 'error',
      });
      return;
    } finally {
      setIsSavingNote(false);
    }

    setNoteMessage('');
    toast({
      description: 'Observação adicionada ao histórico.',
      status: 'success',
    });
  }, [budget, noteMessage, getData]);

  return (
    <Modal {...restProps} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        sx={{
          width: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxWidth: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          height: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxHeight: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
        }}
      >
        <ModalHeader>Histórico de aprovação</ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <Tabs
            index={activeTab}
            onChange={(index) => setActiveTab(index as 0 | 1)}
            variant="unstyled"
            isLazy
          >
            <TabList borderBottom="1px solid" borderColor="gray.200" mb={4}>
              <Tab
                px={0}
                pb={2}
                mr={6}
                fontWeight="semibold"
                _selected={{
                  color: 'blue.500',
                  borderBottom: '2px solid',
                  borderColor: 'blue.500',
                }}
                _focus={{ boxShadow: 'none' }}
              >
                Anotações
              </Tab>
              <Tab
                px={0}
                pb={2}
                fontWeight="semibold"
                _selected={{
                  color: 'blue.500',
                  borderBottom: '2px solid',
                  borderColor: 'blue.500',
                }}
                _focus={{ boxShadow: 'none' }}
              >
                Histórico
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel p={0}>
                <Flex direction="row" gap={1}>
                  <Text fontWeight="bold">Descrição: </Text>
                  <Skeleton isLoaded={!loadingBudget} minH="20px" flex="1">
                    <Text
                      noOfLines={1}
                      textOverflow="ellipsis"
                      title={budget.identification?.description}
                    >
                      {loadingBudget
                        ? ''
                        : budget.identification?.description ||
                          'Não especificado'}
                    </Text>
                  </Skeleton>
                </Flex>

                <Grid
                  w="100%"
                  templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
                  columnGap={2}
                  rowGap={4}
                  paddingY={4}
                  marginBottom={4}
                >
                  <Flex direction="row" gap={1}>
                    <Text fontWeight="bold">Status atual: </Text>
                    <Skeleton isLoaded={!loadingBudget} minH="20px" flex="1">
                      <Text noOfLines={1} textOverflow="ellipsis">
                        {loadingBudget
                          ? ''
                          : budget.flow_status?.name || 'Não especificado'}
                      </Text>
                    </Skeleton>
                  </Flex>

                  <Flex direction="row" gap={1}>
                    <Text fontWeight="bold">Criação: </Text>
                    <Skeleton isLoaded={!loadingBudget} minH="20px" flex="1">
                      <Text noOfLines={1} textOverflow="ellipsis">
                        {loadingBudget || !budget.created_at
                          ? ''
                          : formatDate(budget.created_at)}
                      </Text>
                    </Skeleton>
                  </Flex>

                  {(!!budget.user || loadingBudget) && (
                    <Flex direction="row" gap={1}>
                      <Text fontWeight="bold">Usuário: </Text>
                      <Skeleton isLoaded={!loadingBudget} minH="20px" flex="1">
                        <Text>
                          {loadingBudget ? '' : getBudgetUserName(budget.user)}
                        </Text>
                      </Skeleton>
                    </Flex>
                  )}

                  <Flex direction="row" gap={1}>
                    <Text fontWeight="bold">Total: </Text>
                    <Skeleton isLoaded={!loadingBudget} minH="20px" flex="1">
                      <Text>
                        {loadingBudget
                          ? ''
                          : Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            }).format(budget.total)}
                      </Text>
                    </Skeleton>
                  </Flex>
                </Grid>

                <Divider my={4} />

                <FormControl>
                  <FormLabel fontWeight="bold">Inserir observação</FormLabel>
                  <Textarea
                    value={noteMessage}
                    onChange={(event) => setNoteMessage(event.target.value)}
                    placeholder="Escreva uma observação sobre o orçamento"
                    minH="120px"
                    resize="vertical"
                  />
                </FormControl>

                <Box display="flex" justifyContent="flex-end" mt={4}>
                  <Button
                    colorScheme="blue"
                    onClick={handleSaveNote}
                    isDisabled={!noteMessage.trim() || isSavingNote}
                    isLoading={isSavingNote}
                  >
                    Salvar observação
                  </Button>
                </Box>
              </TabPanel>

              <TabPanel p={0}>
                <AppTable
                  cols={[
                    {
                      field: 'responsible',
                      description: 'Responsável',
                      sortable: false,
                    },
                    { field: 'status', description: 'Status', sortable: false },
                    {
                      field: 'message',
                      description: 'Mensagem',
                      sortable: false,
                    },
                  ]}
                  mapping={(item) => ({
                    id: item.id,
                    responsible: (
                      <>
                        <Text mb={1}>{formatDate(item.created_at)}</Text>

                        <Text fontWeight="bold" fontSize="smaller">
                          {getUserName(item.user)}
                        </Text>
                      </>
                    ),
                    status: item.status && (
                      <FlowStatusBadge
                        color={item.status?.color}
                        description={item.status?.name}
                      />
                    ),
                    message: <Text whiteSpace="pre-wrap">{item.message}</Text>,
                    actions: null,
                  })}
                  data={history}
                  noDataMessage="Nenhuma ação registrada."
                  loading={loading}
                  onUpdateSort={() => {
                    //
                  }}
                />

                <PaginationWrapper
                  lastPage={servicePagination.last_page}
                  onPaginate={(selectedPage) => {
                    setPagination({ ...pagination, page: selectedPage });
                  }}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={2}>
            <Button
              isLoading={loading}
              isDisabled={loading}
              onClick={handleConfirm}
            >
              Fechar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetHistory;
